import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import axios from 'axios';
import API_URL from '../config/api';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [presenceStats, setPresenceStats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      return;
    }

    const sessionToken = typeof window !== 'undefined'
      ? window.localStorage.getItem('token')
      : '';

    // Create socket connection
    // HARDCODED para Railway - detectar producción por hostname o Capacitor
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    // Detect private network IPs (same LAN development: 192.168.x.x, 10.x.x.x, 172.16-31.x.x)
    const isPrivateIP = /^10\./.test(hostname) || /^192\.168\./.test(hostname) || /^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname);
    const isLocalDev = isLocalhost || isPrivateIP;

    const isNativeCapacitor =
      typeof window !== 'undefined' &&
      !!window.Capacitor &&
      (window.location.protocol === 'capacitor:' || window.location.origin.startsWith('capacitor://'));

    const isProduction =
      !isLocalDev &&
      (hostname === 'mundoxyz.com' ||
        hostname === 'www.mundoxyz.com' ||
        hostname.endsWith('.mundoxyz.com') ||
        hostname.endsWith('.mundoxyz') ||
        hostname.includes('mundoxyz') ||
        hostname.includes('railway.app') ||
        isNativeCapacitor);

    // Determine the socket URL
    let socketUrl;
    if (isProduction) {
      // Production/Capacitor: prefer configured backend URL
      socketUrl = (axios?.defaults?.baseURL || API_URL || 'https://mundoxyz.com')
        .toString()
        .replace(/\/$/, '');
      console.log('🔌 Socket conectando a producción:', socketUrl);
    } else if (isPrivateIP) {
      // Same LAN: backend runs on same host as frontend, port 3000
      socketUrl = `http://${hostname}:3000`;
      console.log('🔌 Socket conectando a LAN privada:', socketUrl);
    } else {
      // Development localhost
      socketUrl = 'http://localhost:3000';
      console.log('🔌 Socket conectando a desarrollo (backend):', socketUrl);
    }
    
    console.log('🌍 Hostname actual:', window.location.hostname);
    console.log('🔧 isProduction:', isProduction);
    
    const transports = isNativeCapacitor ? ['websocket'] : ['websocket', 'polling'];

    const newSocket = io(socketUrl, {
      auth: {
        token: sessionToken,
        userId: user.id,
        username: user.username
      },
      transports,
      // Reconexión activa en todos los entornos para sobrevivir reinicios del backend
      // (nodemon en dev, despliegues en prod). Delays más cortos en dev para menos spam.
      reconnection: true,
      reconnectionDelay: isProduction ? 3000 : 1000,
      reconnectionDelayMax: isProduction ? 10000 : 3000,
      reconnectionAttempts: isProduction ? 10 : 5,
      timeout: 20000,
      // Capacitor specific options
      forceNew: false,
      upgrade: true,
      rememberUpgrade: true,
      // Additional options for mobile
      autoConnect: true,
      forceJSONP: false
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setConnected(true);

      try {
        const t = newSocket?.io?.engine?.transport?.name;
        if (t) {
          console.log('[socket] transport:', t);
        }
      } catch (_) {
        // ignore
      }

      try {
        newSocket.emit('presence:ping', { userId: user.id });
      } catch (_) {
        // ignore
      }
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    newSocket.on('presence:stats', (stats) => {
      try {
        setPresenceStats(stats);
      } catch (_) {
        // ignore
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });

    try {
      const engine = newSocket?.io?.engine;
      if (engine) {
        engine.on('upgrade', (transport) => {
          try {
            console.log('[socket] upgrade transport:', transport?.name);
          } catch (_) {
            // ignore
          }
        });
        engine.on('close', (reason) => {
          try {
            console.log('[socket] engine close:', reason);
          } catch (_) {
            // ignore
          }
        });
      }
    } catch (_) {
      // ignore
    }

    // Set socket
    setSocket(newSocket);

    // Cleanup on unmount or when user changes
    return () => {
      newSocket.disconnect();
      setConnected(false);
      setSocket(null);
      setPresenceStats(null);
    };
  }, [user?.id, user?.username]);

  useEffect(() => {
    if (!socket || !connected || !user?.id) return;
    const id = setInterval(() => {
      try {
        socket.emit('presence:ping', { userId: user.id });
      } catch (_) {
        // ignore
      }
    }, 30000);
    return () => clearInterval(id);
  }, [socket, connected, user?.id]);

  const value = React.useMemo(() => ({
    socket,
    connected,
    presenceStats
  }), [socket, connected, presenceStats]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
